import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { useRef } from "react"
import { resetPassword, signInWithMagicLink, signInWithPassword, signInWithProvider, signUp, updatePassword } from "@/utils/supabase-queries"
import supabaseClient from "@/utils/supabase-browser"
import { AuthProvider } from "@/types"

export const useSignInWithMagicLink = ({ onSuccess, onMutate, onError }: { onSuccess?: () => void; onMutate?: () => void; onError?: (error: unknown) => void }) => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ email }: { email: string }) => {
            return signInWithMagicLink(supabaseClient, email)
        },
        {
            onMutate: () => {
                toastRef.current = toast.loading("Sending magic link...")
                onMutate?.()
            },
            onSuccess: () => {
                toast.success("Check your email for the magic link!", {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onSuccess?.()
            },
            onError: (error) => {
                toast.error(String(error), {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onError?.(error)
            }
        }
    )
}

export const useSignInWithPassword = ({ onSuccess, onMutate, onError }: { onSuccess?: () => void; onMutate?: () => void; onError?: (error: unknown) => void }) => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ email, password }: { email: string; password: string }) => {
            return signInWithPassword(supabaseClient, email, password)
        },
        {
            onMutate: () => {
                toastRef.current = toast.loading("Signing in...")
                onMutate?.()
            },
            onSuccess: () => {
                toast.success("Signed in!", {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onSuccess?.()
            },
            onError: (error) => {
                toast.error(String(error), {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onError?.(error)
            }
        }
    )
}

export const useResetPassword = ({ onSuccess, onMutate, onError }: { onSuccess?: () => void; onMutate?: () => void; onError?: (error: unknown) => void }) => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ email }: { email: string }) => {
            return resetPassword(supabaseClient, email)
        },
        {
            onMutate: () => {
                toastRef.current = toast.loading("Sending password reset email...")
                onMutate?.()
            },
            onSuccess: () => {
                toast.success("Check your email for the password reset link!", {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onSuccess?.()
            },
            onError: (error) => {
                toast.error(String(error), {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onError?.(error)
            }
        }
    )
}

export const useUpdatePassword = ({ onSuccess, onMutate, onError }: { onSuccess?: () => void; onMutate?: () => void; onError?: (error: unknown) => void }) => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ password }: { password: string }) => {
            return updatePassword(supabaseClient, password)
        },
        {
            onMutate: () => {
                toastRef.current = toast.loading("Updating password...")
                onMutate?.()
            },
            onSuccess: () => {
                toast.success("Password updated!", {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onSuccess?.()
            },
            onError: (error) => {
                toast.error(String(error), {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onError?.(error)
            }
        }
    )
}

export const useSignInWithProvider = () => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ provider }: { provider: AuthProvider }) => {
            return signInWithProvider(supabaseClient, provider)
        },
        {
            onMutate: ({ provider }) => {
                toastRef.current = toast.loading(`Signing in with ${provider}...`)
            }
        }
    )
}

export const useSignUp = ({ onSuccess, onMutate, onError }: { onSuccess?: () => void; onMutate?: () => void; onError?: (error: unknown) => void }) => {
    const toastRef = useRef<string | number | null>(null)
    return useMutation(
        async ({ email, password }: { email: string; password: string }) => {
            return signUp(supabaseClient, email, password)
        },
        {
            onMutate: () => {
                toastRef.current = toast.loading("Signing up...")
                onMutate?.()
            },
            onSuccess: () => {
                toast.success("Signed up!", {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onSuccess?.()
            },
            onError: (error) => {
                toast.error(String(error), {
                    id: toastRef.current || ""
                })
                toastRef.current = null
                onError?.(error)
            }
        }
    )
}
